import type { SelectedCommunityMember } from "@circle-react/components/shared/CommunityMemberSelectCompass";
import { useCallback, useMemo, useState } from "react";

import type { MemberEntry } from "../MembersToAddList/types";

interface UseSelectedMembersToAddParams {
  memberIds: string[];
  onMemberIdsChange: (memberIds: string[]) => void;
}

export const useSelectedMembersToAdd = ({
  memberIds,
  onMemberIdsChange,
}: UseSelectedMembersToAddParams) => {
  const [selectedMembers, setSelectedMembers] = useState<MemberEntry[]>([]);

  const membersToAdd = useMemo(() => {
    const selectedMembersById = new Map(
      selectedMembers.map(member => [String(member.id), member]),
    );

    return memberIds
      .map(memberId => selectedMembersById.get(String(memberId)))
      .filter((member): member is MemberEntry => Boolean(member));
  }, [memberIds, selectedMembers]);

  const handleSelectMember = useCallback((member: SelectedCommunityMember) => {
    setSelectedMembers(currentMembers => {
      if (
        currentMembers.some(
          currentMember => String(currentMember.id) === String(member.id),
        )
      ) {
        return currentMembers;
      }

      return [
        ...currentMembers,
        {
          id: member.id,
          name: member.name,
          email: member.email,
          avatarUrl: member.avatarUrl,
        },
      ];
    });
  }, []);

  const removeMemberId = useCallback(
    (memberId: number | string) => {
      onMemberIdsChange(memberIds.filter(id => id !== String(memberId)));
      setSelectedMembers(currentMembers =>
        currentMembers.filter(member => String(member.id) !== String(memberId)),
      );
    },
    [memberIds, onMemberIdsChange],
  );

  return {
    membersToAdd,
    handleSelectMember,
    removeMemberId,
  };
};
